/**
 * Shared tool shell — one renderer for every declarative micro-tool.
 * Upgraded contract (all optional, fully backward compatible):
 *   field.hint      — guidance text under the input
 *   def.about       — "How it works" explainer (string or [paragraphs])
 *   def.run(v) may return:
 *     string | [{label, value}]                                  (as before)
 *     { rows: [{label, value}], note: "interpretation…",
 *       table: { head: […], rows: [[…]] },
 *       viz: { title, bars: [{label, value, display, color?}] } }   (rich result)
 * Plus for every tool: scenario save/compare (localStorage), copy summary,
 * and a one-click PDF report of inputs + results.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, UploadCloud, FileDown, Copy, Info, ChevronDown, ChevronUp, GitCompareArrows } from "lucide-react";

const VIZ_COLORS = ["#4f46e5", "#f97316", "#0ea5e9", "#10b981", "#e11d48", "#a855f7", "#f59e0b"];

export const inputCls = "w-full border border-slate-200 bg-white rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors font-mono text-sm";
export const labelCls = "text-xs font-semibold uppercase tracking-widest text-slate-500 block mb-2";
export const btnCls = "inline-flex items-center gap-2 bg-indigo-600 text-white rounded-lg px-5 py-2.5 font-medium hover:bg-indigo-700 active:scale-95 transition disabled:opacity-50";
export const ghostCls = "inline-flex items-center gap-2 border border-slate-200 rounded-lg px-4 py-2.5 font-medium hover:bg-slate-50 active:scale-95 transition text-sm";

const copyText = (t) => { navigator.clipboard.writeText(String(t)); toast.success("Copied"); };
const SCEN_KEY = (slug) => `tool-scenarios-${slug}`;

function loadScenarios(slug) {
  try { return JSON.parse(localStorage.getItem(SCEN_KEY(slug)) || "[]"); } catch { return []; }
}

export function MicroShell({ def }) {
  const initial = Object.fromEntries(def.fields.map((f) => [
    f.key,
    f.def ?? (f.type === "select" ? (f.options[0]?.value ?? f.options[0]) : ""),
  ]));
  const [values, setValues] = useState(initial);
  const [out, setOut] = useState(null); // normalized: { rows, note?, table? }
  const [scenarios, setScenarios] = useState(() => loadScenarios(def.slug));
  const [showAbout, setShowAbout] = useState(false);
  const [exporting, setExporting] = useState(false);

  const run = (vals) => {
    const v = vals || values;
    try {
      const r = def.run(v);
      const norm = Array.isArray(r) ? { rows: r }
        : typeof r === "object" && r !== null && r.rows ? r
        : { rows: [{ label: "Result", value: String(r) }] };
      setOut(norm);
      return norm;
    } catch (e) {
      toast.error(e.message || "Check your inputs");
      return null;
    }
  };

  const fieldLabel = (key) => def.fields.find((f) => f.key === key)?.label || key;

  const summaryText = () => {
    if (!out) return "";
    const lines = [`${def.name} — juliusmason.com/tools/${def.slug}`, ""];
    def.fields.forEach((f) => lines.push(`${f.label}: ${values[f.key]}`));
    lines.push("");
    out.rows.forEach((r) => lines.push(`${r.label}: ${r.value}`));
    if (out.note) lines.push("", out.note);
    if (out.table) {
      lines.push("", out.table.head.join(" | "));
      out.table.rows.forEach((r) => lines.push(r.join(" | ")));
    }
    return lines.join("\n");
  };

  const exportPdf = async () => {
    if (!out) return;
    setExporting(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const M = 15;
      let y = 20;
      doc.setFont("helvetica", "bold"); doc.setFontSize(18);
      doc.text(def.name, M, y); y += 7;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor("#64748B");
      doc.text(`Generated ${new Date().toLocaleString()} · juliusmason.com/tools/${def.slug}`, M, y); y += 10;
      doc.setTextColor("#0F172A");

      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Your inputs", M, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      def.fields.forEach((f) => {
        const opt = f.type === "select" ? (f.options.find((o) => (o.value ?? o) === values[f.key])?.label ?? values[f.key]) : values[f.key];
        doc.text(`${f.label}:  ${opt}`, M, y); y += 5.5;
      });
      y += 4;

      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Results", M, y); y += 6;
      out.rows.forEach((r) => {
        doc.setFont("helvetica", "normal"); doc.setFontSize(10);
        doc.text(String(r.label), M, y);
        doc.setFont("courier", "bold");
        doc.text(String(r.value), 195 - M, y, { align: "right" });
        y += 5.5;
      });
      if (out.note) {
        y += 4;
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor("#475569");
        doc.splitTextToSize(out.note, 180).forEach((l) => { doc.text(l, M, y); y += 4.5; });
        doc.setTextColor("#0F172A");
      }
      if (out.table) {
        y += 5;
        doc.setFont("helvetica", "bold"); doc.setFontSize(9);
        const cols = out.table.head.length;
        const cw = 180 / cols;
        out.table.head.forEach((h, i) => doc.text(String(h), M + i * cw, y)); y += 2;
        doc.line(M, y, M + 180, y); y += 4;
        doc.setFont("courier", "normal"); doc.setFontSize(8.5);
        out.table.rows.slice(0, 40).forEach((row) => {
          if (y > 280) { doc.addPage(); y = 20; }
          row.forEach((cell, i) => doc.text(String(cell), M + i * cw, y));
          y += 4.5;
        });
      }
      doc.save(`${def.slug}-report.pdf`);
      toast.success("PDF report downloaded");
    } catch { toast.error("PDF export failed"); }
    finally { setExporting(false); }
  };

  const saveScenario = () => {
    if (!out) return;
    const snap = { when: new Date().toISOString(), values: { ...values }, rows: out.rows.slice(0, 4) };
    const next = [snap, ...scenarios].slice(0, 4);
    setScenarios(next);
    localStorage.setItem(SCEN_KEY(def.slug), JSON.stringify(next));
    toast.success(`Scenario ${next.length} saved — tweak inputs to compare`);
  };

  const dropScenario = (i) => {
    const next = scenarios.filter((_, j) => j !== i);
    setScenarios(next);
    localStorage.setItem(SCEN_KEY(def.slug), JSON.stringify(next));
  };

  const loadScenario = (s) => {
    setValues((v) => ({ ...v, ...s.values }));
    toast.success("Scenario loaded into the inputs");
  };

  const wide = def.fields.length > 1;

  return (
    <div>
      {/* inputs */}
      <div className={`grid gap-4 ${wide ? "sm:grid-cols-2" : ""}`}>
        {def.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className={labelCls}>{f.label}</label>
            {f.type === "textarea" ? (
              <textarea rows={f.rows || 5} className={inputCls} placeholder={f.ph || ""} value={values[f.key]}
                data-testid={`${def.slug}-${f.key}`} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
            ) : f.type === "select" ? (
              <select className={inputCls} value={values[f.key]} data-testid={`${def.slug}-${f.key}`}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}>
                {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
              </select>
            ) : (
              <input type={f.type || "text"} className={inputCls} placeholder={f.ph || ""} value={values[f.key]}
                data-testid={`${def.slug}-${f.key}`} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
            )}
            {f.hint && <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">{f.hint}</p>}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-5 flex-wrap">
        <button className={btnCls} onClick={() => run()} data-testid={`${def.slug}-run`}>{def.action || "Calculate"}</button>
        {def.about && (
          <button onClick={() => setShowAbout(!showAbout)} className={ghostCls} data-testid={`${def.slug}-about-toggle`}>
            <Info className="w-4 h-4" /> How it works {showAbout ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {showAbout && def.about && (
        <div className="mt-4 border border-indigo-100 bg-indigo-50/50 rounded-lg p-4 text-sm text-slate-600 space-y-2 leading-relaxed" data-testid={`${def.slug}-about`}>
          {(Array.isArray(def.about) ? def.about : [def.about]).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}

      {/* results */}
      {out && (
        <div className="mt-6" data-testid={`${def.slug}-result`}>
          <div className="space-y-2">
            {out.rows.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-4 border border-slate-100 bg-slate-50/60 rounded-lg px-4 py-3">
                <span className="text-sm text-slate-500">{r.label}</span>
                <span className="font-mono text-sm font-semibold text-right break-all whitespace-pre-wrap">{r.value}</span>
                <button onClick={() => copyText(r.value)} className="text-indigo-600 text-xs font-medium hover:underline flex-shrink-0">Copy</button>
              </div>
            ))}
          </div>
          {out.note && (
            <p className="mt-4 text-sm text-slate-600 border-l-2 border-indigo-300 pl-3 leading-relaxed" data-testid={`${def.slug}-note`}>{out.note}</p>
          )}
          {out.viz && out.viz.bars?.length > 0 && (
            <div className="mt-4 border border-slate-100 rounded-lg p-4 bg-white" data-testid={`${def.slug}-viz`}>
              {out.viz.title && <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{out.viz.title}</p>}
              <div className="space-y-2.5">
                {out.viz.bars.map((b, i) => {
                  const max = Math.max(...out.viz.bars.map((x) => Math.abs(x.value) || 0), 0.0001);
                  return (
                    <div key={i}>
                      <div className="flex justify-between gap-3 text-xs mb-1">
                        <span className="text-slate-500">{b.label}</span>
                        <span className="font-mono font-semibold">{b.display}</span>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-[width] duration-700 ease-out"
                          style={{ width: `${Math.min(100, Math.max(2, Math.abs(b.value) / max * 100))}%`, backgroundColor: b.color || VIZ_COLORS[i % VIZ_COLORS.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {out.table && (
            <div className="mt-4 overflow-x-auto border border-slate-100 rounded-lg" data-testid={`${def.slug}-table`}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                    {out.table.head.map((h, i) => <th key={i} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {out.table.rows.map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      {row.map((c, j) => <td key={j} className="px-3 py-1.5 whitespace-nowrap">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center gap-2.5 mt-4 flex-wrap">
            <button onClick={saveScenario} className={ghostCls} data-testid={`${def.slug}-save-scenario`}>
              <Save className="w-4 h-4" /> Save scenario{scenarios.length ? ` (${scenarios.length}/4)` : ""}
            </button>
            <button onClick={() => copyText(summaryText())} className={ghostCls} data-testid={`${def.slug}-copy-summary`}>
              <Copy className="w-4 h-4" /> Copy summary
            </button>
            <button onClick={exportPdf} disabled={exporting} className={ghostCls} data-testid={`${def.slug}-export-pdf`}>
              <FileDown className="w-4 h-4" /> {exporting ? "Building…" : "PDF report"}
            </button>
          </div>
        </div>
      )}

      {/* scenario compare */}
      {scenarios.length > 0 && (
        <div className="mt-6" data-testid={`${def.slug}-scenarios`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2.5 flex items-center gap-1.5">
            <GitCompareArrows className="w-3.5 h-3.5" /> Saved scenarios
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {scenarios.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3.5 bg-white" data-testid={`${def.slug}-scenario-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] text-slate-400">Scenario {scenarios.length - i} · {new Date(s.when).toLocaleDateString()}</span>
                  <span className="flex gap-1">
                    <button onClick={() => loadScenario(s)} className="text-indigo-600 text-xs font-medium hover:underline inline-flex items-center gap-1" data-testid={`${def.slug}-scenario-load-${i}`}>
                      <UploadCloud className="w-3 h-3" /> Load
                    </button>
                    <button onClick={() => dropScenario(i)} className="text-slate-400 hover:text-red-500 px-1" data-testid={`${def.slug}-scenario-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </span>
                </div>
                <div className="space-y-1">
                  {s.rows.map((r, j) => (
                    <div key={j} className="flex justify-between gap-3 text-xs">
                      <span className="text-slate-500">{r.label}</span>
                      <span className="font-mono font-semibold">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const makeTool = (def) => ({ pro: false, span: "md:col-span-1", ...def, Component: () => <MicroShell def={def} /> });
export const makeField = (key, label, extra = {}) => ({ key, label, ...extra });
